import { useMemo } from 'react';

import {
  PinMapEditor,
  type PinMapItem,
  type PinMapLegendEntry,
} from '@/components/shared/map/PinMapEditor';
import { fallbackPinColor, PIN_UNASSIGNED_COLOR } from '@/components/shared/map/pinColors';
import { useUpdateCoords } from '@/components/events/map/useUpdateCoords';
import type { EventChallenge, EventChallengeGroup } from '@/config/objects/events';

/** Minimal stack shape the map needs (full type lives in EventStacksTab). */
export interface MapStack {
  id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
}

/** Minimal artifact shape the map needs. */
export interface MapArtifact {
  id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  detectionRadius?: number | null;
}

export interface EventMapEditorProps {
  eventId: string;
  groups: EventChallengeGroup[];
  stacks: MapStack[];
  artifacts: MapArtifact[];
  googleMapsApiKey?: string;
  /** Drag/placement disabled when the event can no longer be edited. */
  editable: boolean;
}

const STACK_COLOR = '#0ea5e9';
const ARTIFACT_COLOR = '#a855f7';

function groupColor(group: EventChallengeGroup, index: number): string {
  return group.color || fallbackPinColor(index) || PIN_UNASSIGNED_COLOR;
}

/** Flattens groups/stacks/artifacts into one pin list with colors. */
function buildItems(
  groups: EventChallengeGroup[],
  stacks: MapStack[],
  artifacts: MapArtifact[],
): PinMapItem[] {
  const challengeItems: PinMapItem[] = groups.flatMap((group, index) =>
    group.challenges.map((challenge: EventChallenge) => ({
      kind: 'challenge' as const,
      id: challenge.id,
      name: challenge.name,
      latitude: challenge.latitude ?? null,
      longitude: challenge.longitude ?? null,
      color: groupColor(group, index),
      groupName: group.name,
    })),
  );
  const stackItems: PinMapItem[] = stacks.map((stack) => ({
    kind: 'stack' as const,
    id: stack.id,
    name: stack.name,
    latitude: stack.latitude,
    longitude: stack.longitude,
    color: STACK_COLOR,
  }));
  const artifactItems: PinMapItem[] = artifacts.map((artifact) => ({
    kind: 'artifact' as const,
    id: artifact.id,
    name: artifact.name,
    latitude: artifact.latitude,
    longitude: artifact.longitude,
    color: ARTIFACT_COLOR,
    radius: artifact.detectionRadius,
  }));
  return [...challengeItems, ...stackItems, ...artifactItems];
}

/**
 * The event map editor: every challenge (colored by group), stack, and artifact
 * on one map. Drag a pin to move it; items without coordinates are listed in the
 * "Needs a location" panel — arm one, then click the map to place it. Thin
 * adapter over the shared {@link PinMapEditor}.
 */
export function EventMapEditor({
  eventId,
  groups,
  stacks,
  artifacts,
  googleMapsApiKey,
  editable,
}: EventMapEditorProps) {
  const updateCoords = useUpdateCoords(eventId);

  const items = useMemo(() => buildItems(groups, stacks, artifacts), [groups, stacks, artifacts]);

  const legend = useMemo<PinMapLegendEntry[]>(() => {
    const entries: PinMapLegendEntry[] = groups.map((group, index) => ({
      label: group.name,
      color: groupColor(group, index),
    }));
    if (stacks.length > 0) entries.push({ label: 'Stacks', color: STACK_COLOR, shape: 'diamond' });
    if (artifacts.length > 0) entries.push({ label: 'Artifacts', color: ARTIFACT_COLOR });
    return entries;
  }, [groups, stacks, artifacts]);

  return (
    <PinMapEditor
      items={items}
      editable={editable}
      legend={legend}
      fillViewport
      googleMapsApiKey={googleMapsApiKey}
      onMove={(item, latitude, longitude) =>
        updateCoords.mutate({ kind: item.kind, entityId: item.id, latitude, longitude })
      }
    />
  );
}
